import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeadManager from '@/components/base/HeadManager';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('chapters');

  const comic = useMemo(() => comics.find((c) => c.slug === slug), [slug]);
  const categoryInfo = comic ? categories.find((cat) => cat.slug === comic.category) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!comic) {
    return (
      <>
        <HeadManager title="漫画未找到 - 香香漫画" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-question-line text-4xl text-foreground-400 w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const chapterList = Array.from({ length: Math.min(comic.chapters, 50) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(comic.updatedAt).getTime() - i * 86400000,
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 香香漫画`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2024-01-15',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '香香漫画',
      url: 'https://xiangxiangmanga.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://xiangxiangmanga.com/comic/${comic.slug}`,
    },
  };

  return (
    <>
      <HeadManager
        title={`${comic.title} - 香香漫画免费看 | 香香漫画`}
        description={`${comic.title}，作者${comic.author}。${comic.description.slice(0, 100)}香香漫画免费在线阅读，下拉式追更体验。`}
        keywords={`香香漫画,${comic.title},${comic.author},免费漫画,在线漫画,${categoryInfo?.name || ''}漫画`}
        canonical={`https://xiangxiangmanga.com/comic/${comic.slug}`}
        ogTitle={`${comic.title} - 香香漫画`}
        ogDescription={comic.description}
        ogType="article"
        ogUrl={`https://xiangxiangmanga.com/comic/${comic.slug}`}
        ogImage={comic.cover}
        scripts={[{ type: 'application/ld+json', content: JSON.stringify(articleSchema) }]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category' },
                { label: categoryInfo?.name || '漫画', href: `/category?tab=${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0 flex flex-col items-center lg:items-start">
                <div className="w-[220px] h-[293px] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 香香漫画免费阅读`}
                    title={`${comic.title} | 香香漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 flex gap-2 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-foreground-600 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-600">状态</p>
                    <p className={`font-heading text-lg ${comic.status === '已完结' ? 'text-secondary-600' : 'text-primary-500'}`}>
                      {comic.status}
                    </p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-600">评分</p>
                    <p className="font-heading text-lg text-accent-500">{comic.rating}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-600">人气</p>
                    <p className="font-heading text-lg text-foreground-950">{(comic.views / 10000).toFixed(0)}万</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-3 text-center">
                    <p className="text-xs text-foreground-600">总章节</p>
                    <p className="font-heading text-lg text-foreground-950">{comic.chapters}话</p>
                  </div>
                </div>

                <div className="flex gap-3 mb-6">
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      activeTab === 'chapters'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                    }`}
                  >
                    章节列表
                  </button>
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      activeTab === 'info'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-100'
                    }`}
                  >
                    作品介绍
                  </button>
                </div>

                {activeTab === 'info' && (
                  <div className="bg-background-100 rounded-lg p-6">
                    <h2 className="font-heading text-xl text-foreground-950 mb-3">作品介绍</h2>
                    <p className="text-foreground-700 leading-relaxed text-sm">{comic.description}</p>
                    <div className="mt-4 pt-4 border-t border-background-200">
                      <p className="text-sm text-foreground-600">
                        <span className="font-medium">分类：</span>
                        {categoryInfo?.name || comic.category}
                      </p>
                      <p className="text-sm text-foreground-600 mt-1">
                        <span className="font-medium">最近更新：</span>
                        {comic.updatedAt}
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'chapters' && (
                  <div className="bg-background-100 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-xl text-foreground-950">章节列表</h2>
                      <div className="flex items-center gap-2 text-sm text-foreground-600">
                        <span>共 {comic.chapters} 话</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700">
                          {comic.status === '连载中' ? '每周更新' : '已完结'}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {chapterList.map((ch) => (
                        <a
                          key={ch.num}
                          href="#"
                          className="block px-3 py-2 rounded bg-background-50 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors no-underline text-center border border-background-200 hover:border-primary-200"
                          onClick={(e) => e.preventDefault()}
                        >
                          {ch.title}
                        </a>
                      ))}
                    </div>

                    {comic.chapters > 50 && (
                      <div className="text-center mt-4">
                        <p className="text-sm text-foreground-500">
                          以上为最近50话，更多章节敬请期待
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                {relatedComics.map((c) => (
                  <div key={c.id} className="flex justify-center">
                    <ComicCard comic={c} size="small" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}